import React, { useMemo } from 'react';
import type { EChartsOption } from 'echarts';
import BaseChart from '../../../Charts/BaseChart';
import type { NebulaMessageVolumePoint } from '../../../../types/nebula-insights';
import {
  ASSISTANT_MESSAGE_COLOR,
  USER_MESSAGE_COLOR,
  dashboardAreaGradient,
  dashboardAxisTooltip,
  dashboardCategoryAxis,
  dashboardGrid,
  dashboardValueAxis,
} from '../chartTheme';

interface MessageVolumeChartProps {
  data: NebulaMessageVolumePoint[];
  loading?: boolean;
}

const MessageVolumeChart: React.FC<MessageVolumeChartProps> = ({ data, loading = false }) => {
  const option = useMemo<EChartsOption>(() => {
    const labels = data.map((point) => point.date);
    return {
      backgroundColor: '#ffffff',
      tooltip: {
        ...dashboardAxisTooltip,
        trigger: 'axis',
      },
      legend: {
        data: ['User', 'Assistant'],
        bottom: 0,
        left: 'center',
        textStyle: { color: '#1f2937', fontSize: 12 },
      },
      grid: dashboardGrid,
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: labels,
        ...dashboardCategoryAxis,
      },
      yAxis: {
        type: 'value',
        ...dashboardValueAxis,
      },
      series: [
        {
          name: 'User',
          type: 'line',
          smooth: true,
          showSymbol: false,
          data: data.map((point) => point.user),
          lineStyle: { width: 3, color: USER_MESSAGE_COLOR },
          itemStyle: {
            color: USER_MESSAGE_COLOR,
            borderColor: '#ffffff',
            borderWidth: 2,
          },
          areaStyle: {
            color: dashboardAreaGradient(USER_MESSAGE_COLOR),
            opacity: 0.25,
          },
        },
        {
          name: 'Assistant',
          type: 'line',
          smooth: true,
          showSymbol: false,
          data: data.map((point) => point.assistant),
          lineStyle: { width: 3, color: ASSISTANT_MESSAGE_COLOR },
          itemStyle: {
            color: ASSISTANT_MESSAGE_COLOR,
            borderColor: '#ffffff',
            borderWidth: 2,
          },
          areaStyle: {
            color: dashboardAreaGradient(ASSISTANT_MESSAGE_COLOR),
            opacity: 0.25,
          },
        },
      ],
    };
  }, [data]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-5 h-full">
      <div className="mb-4">
        <h3 className="text-sm font-medium text-gray-900 font-family-messina-sans">
          Message volume
        </h3>
        <p className="text-xs text-gray-500 font-family-messina-sans mt-1">
          Daily user and assistant messages
        </p>
      </div>
      <div className="h-[240px]">
        {data.length === 0 && !loading ? (
          <div className="h-full flex items-center justify-center text-sm text-gray-500 font-family-messina-sans">
            No message activity in this period
          </div>
        ) : (
          <BaseChart option={option} loading={loading} style={{ height: '240px' }} />
        )}
      </div>
    </div>
  );
};

export default MessageVolumeChart;
